import React, { useCallback, useEffect, useMemo, useState } from "react";

import { noop } from "lodash";
import styled from "styled-components";

import { Crown } from "@similarweb/icons";
import Sentry from "@similarweb/sentry-logger";
import { colorsPalettes, rgba } from "@similarweb/styles";
import { NavItemBadge, Tabs, OnOffSwitch, PlainTooltip, Checkbox } from "@similarweb/ui-components";

import { isAvailable } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { getNoCategoryItem } from "app/components/React/CategoriesDropdown/CategoryPicker";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { GA4VerifiedContainer } from "app/components/React/GAVerifiedIcon/GA4VerifiedContainer";
import { useGoogleAnalytics } from "app/components/React/GAVerifiedIcon/UseGoogleAnalytics";
import { ScorableTabs } from "app/components/React/ScorableTabs/ScorableTabs";
import { EPrivacyStatus } from "app/components/website-header/WebsiteHeaderReact";
import { useLoading } from "app/custom-hooks/loadingHook";
import { NOT_AVAILABLE } from "app/filters/const";
import { i18nFilter, translate } from "app/filters/i18nFilter";
import { abbrNumberVisitsFilter } from "app/filters/ngFilters";
import { DeduplicationLockScreen } from "app/pages/website-analysis/components/deduplicationLockScreen";
import { getNewVsReturningAverage } from "app/pages/website-analysis/new-vs-returning/getNewVsReturningAverage";
import { BetaBranchLabel } from "app/pages/website-analysis/TrafficAndEngagement/BetaBranch/CommonComponents";
import {
    BadgeContainer,
    CompareTab,
    Icon,
    TabPanelStyled,
    TabWrapperStyle,
} from "app/pages/website-analysis/TrafficAndEngagement/Components/StyledComponents";
import { AvgVisitDuration } from "app/pages/website-analysis/TrafficAndEngagement/Metrics/AvgVisitDuration";
import { BounceRate } from "app/pages/website-analysis/TrafficAndEngagement/Metrics/BounceRate";
import { DeduplicatedAudience } from "app/pages/website-analysis/TrafficAndEngagement/Metrics/DeduplicatedAudience";
import { NewVsReturning } from "app/pages/website-analysis/TrafficAndEngagement/Metrics/NewVsReturning";
import { PagesPerVisit } from "app/pages/website-analysis/TrafficAndEngagement/Metrics/PagesPerVisit";
import { PageViews } from "app/pages/website-analysis/TrafficAndEngagement/Metrics/PageViews";
import { UniqueUsers } from "app/pages/website-analysis/TrafficAndEngagement/Metrics/UniqueUsers";
import { Visits } from "app/pages/website-analysis/TrafficAndEngagement/Metrics/Visits";
import {
    addBetaBranchParam,
    hasOpenAiInsightsPermissions,
} from "app/pages/website-analysis/TrafficAndEngagement/UtilityFunctionsAndConstants/UtilityFunctions";
import Loader from "app/pages/website-analysis/website-content/leading-folders/components/Loader";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { isPaidExcelAllowed } from "app/services/PaidExcelService/isPaidExcelAllowed";
import { allTrackers } from "app/services/track/track";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { getMonthsToDateTooltipText } from "app/UtilitiesAndConstants/UtilityFunctions/monthsToDateUtilityFunctions";
import { StyledHeaderTitle } from "pro-features/pages/conversion/components/benchmarkOvertime/StyledComponents";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type { TabType } from "../MD/Tabs";
import { NEW_VS_RETURNING_TAB_NAME, UNIQUE_USERS_TAB_NAME } from "../MD/Tabs";

import { Insights } from "./insights/Insights";
import type { TrafficAndEngagementResponse } from "./types";

const TitleContainer: any = styled(FlexRow)<{ removeBorderButton: boolean }>`
    padding: 0 21px 0 24px;
    box-sizing: border-box;
    justify-content: space-between;
    align-items: center;
    height: 60px;
    ${({ removeBorderButton }) => !removeBorderButton && `border-bottom: 1px solid ${colorsPalettes.carbon["50"]};`}
`;
TitleContainer.displayName = "TitleContainer";

const HeaderTitle = styled(StyledHeaderTitle)`
    font-size: 20px;
`;

const UnbounceCheckboxWrapper = styled.div`
    margin-right: 16px;
    margin-bottom: 1px;
`;

const MTDLabel = styled.span`
    margin-top: 1px;
    font-size: 14px;
    letter-spacing: 0.2px;
    color: ${rgba(colorsPalettes.carbon[500], 0.8)};
`;

export const MTDTitle = styled(FlexRow)<{ isDisabled?: boolean }>(
    (props) => `
    cursor: pointer;
    gap: 4px;
    ${
        props.isDisabled &&
        `
        cursor: not-allowed;
    `
    }
    ${MTDLabel} {
        opacity: 0.5;
    }
`,
);

const RightSection = styled(FlexRow)`
    align-items: center;
`;

const SingleModeTabsWrapper = styled.div`
    [role="tab"] .SWReactIcons svg path {
        fill: ${colorsPalettes.carbon[200]};
    }

    [role="tab"].selected .SWReactIcons svg path {
        fill: ${colorsPalettes.carbon[200]};
    }
`;

export const noCategoryItem = getNoCategoryItem(
    "traffic_and_engagement.generic_graph.category.dropdown.placeholder.text",
);

export const getTabTypeForTracking = (tabType: TabType): string => {
    return tabType === "engagement" ? "Engagement Overview" : "Traffic Overview";
};

export const getWidgetNameForTracking = (tabType: TabType): string => {
    return tabType === "engagement" ? "EngagementOverTime" : "TrafficOverTime";
};

const fetchData = async (queryParams): Promise<TrafficAndEngagementResponse> => {
    const TRAFFIC_AND_ENGAGEMENT_ENDPOINT = "widgetApi/TrafficAndEngagement/EngagementOverview/Table";
    const fetchService = DefaultFetchService.getInstance();
    const trafficAndEngagementPromise = fetchService.get(TRAFFIC_AND_ENGAGEMENT_ENDPOINT, queryParams);
    const { webSource, keys } = queryParams;
    const { duration } = proNavigator.getParams();
    const isNewVsReturningSupported = isAvailable(swSettings.components.WebNewVsReturning);

    if (
        webSource === devicesTypes.MOBILE ||
        webSource === devicesTypes.DESKTOP ||
        duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS ||
        !isNewVsReturningSupported
    ) {
        const trafficAndEngagement = (await trafficAndEngagementPromise) as TrafficAndEngagementResponse;
        return {
            ...trafficAndEngagement,
            Data: [
                {
                    ...trafficAndEngagement.Data[0],
                },
            ],
        };
    }

    const { newUsersAverage, returningUsersAverage } = await getNewVsReturningAverage(keys);
    const trafficAndEngagement = (await trafficAndEngagementPromise) as TrafficAndEngagementResponse;

    const newVsReturningTabData = (() => {
        if (!newUsersAverage && !returningUsersAverage) {
            return NOT_AVAILABLE;
        }

        const total = newUsersAverage + returningUsersAverage;
        if (!total) {
            return NOT_AVAILABLE;
        }

        const newPct = Math.round((newUsersAverage / total) * 100);
        const returningPct = 100 - newPct;

        return `${newPct}% vs. ${returningPct}%`;
    })();

    const result: TrafficAndEngagementResponse = {
        ...trafficAndEngagement,
        Data: [
            {
                ...trafficAndEngagement.Data[0],
                [NEW_VS_RETURNING_TAB_NAME]: newVsReturningTabData,
            },
        ],
    };

    return result;
};

export const unsupportedCategoryTabs = ["DeduplicatedAudience", NEW_VS_RETURNING_TAB_NAME];
const unsupportedMTDTabs = ["UniqueUsers", "DeduplicatedAudience", NEW_VS_RETURNING_TAB_NAME];
const unsupportedUnBouncedTabs = ["UniqueUsers", "DeduplicatedAudience", "BounceRate", NEW_VS_RETURNING_TAB_NAME];
const mapTabToDataProperty = {
    Visits: "AvgMonthVisits",
    AvgVisitDuration: "AvgVisitDuration",
    PagesPerVisit: "PagesPerVisit",
    BounceRate: "BounceRate",
    UniqueUsers: "UniqueUsers",
    DeduplicatedAudience: "DedupUniqueUsers",
    PageViews: "TotalPagesViews",
    [NEW_VS_RETURNING_TAB_NAME]: NEW_VS_RETURNING_TAB_NAME,
};

export const TrafficAndEngagementTabs = ({
    availableTabs,
    selectedTab,
    showGAApprovedData,
    showBetaBranchData,
    params,
    graphProps,
    hasDedupeClaim,
    selectedMetricQueryParam,
    unBouncedQueryParam,
    monthToDateQueryParam,
    titleKey,
    gaConnectionSupported = true,
    showExcelEngagementPopup = undefined,
}) => {
    const {
        webSource,
        duration,
        comparedDuration,
        country,
        key,
        isWWW,
        [unBouncedQueryParam]: unBounced,
        [monthToDateQueryParam]: mtd,
    } = params;
    const { from, to, compareFrom, compareTo, isWindow } = DurationService.getDurationData(
        duration,
        comparedDuration,
    ).forAPI;
    const { hasGAToken, isGADataLoading, gaPrivacyStatus } = useGoogleAnalytics((params.key ?? "").split(",")[0]);
    const [tableDataQuery, tableDataQueryOps] = useLoading();
    const isPaidExcelPermitted = isPaidExcelAllowed();

    const { i18n, swNavigator } = React.useMemo(
        () => ({
            i18n: i18nFilter(),
            swNavigator: proNavigator,
        }),
        [],
    );

    const isSingleCompare = graphProps.meta.isSingleMode && showBetaBranchData;

    const apiParams = addBetaBranchParam(
        {
            ShouldGetVerifiedData: showGAApprovedData,
            compareTo,
            compareFrom,
            country,
            from,
            includeSubDomains: isWWW === "*",
            isWindow,
            keys: key,
            webSource,
            to,
            timeGranularity: graphProps.meta.is28Days ? "Daily" : "Monthly",
            unBounced: String(unBounced).toLowerCase() === "true" && !showGAApprovedData, // unBounced is not supported for GA Approved data
        },
        showBetaBranchData,
        isSingleCompare,
    );

    React.useEffect(() => {
        tableDataQueryOps
            .load(() => (curGraphProps.meta.isSingleMode && key ? fetchData(apiParams) : null))
            .catch((e) => Sentry.fatal("Error in Traffic and engagement", e));
    }, [
        showGAApprovedData,
        params.webSource,
        params.duration,
        params.comparedDuration,
        params.country,
        params.unBounced,
        params.key,
        params.isWWW,
        graphProps.meta.is28Days,
        showBetaBranchData,
        isSingleCompare,
    ]);

    const tabs = React.useMemo(() => {
        const { webSource, comparedDuration } = params;
        const isAlternative = !!tableDataQuery.data?.FromAlternativeSources;
        let tabs = availableTabs.map((tab) => ({
            ...tab,
            ...(tab.getTitle && { title: tab.getTitle(graphProps.meta.is28Days) }),
            format:
                tab.name === "Visits" && isAlternative && !comparedDuration
                    ? "abbrNumberWithNASupportVisits"
                    : tab.format,
        }));

        if (!graphProps.meta.isSingleMode) {
            tabs = availableTabs.map(({ value, ...tabProps }) => tabProps);
        } else if (tableDataQuery.data) {
            const webSourceData = tableDataQuery.data.Data.find((i) => i.Source === webSource);
            tabs = tabs.map((tab) => {
                let value = webSourceData?.[mapTabToDataProperty[tab.name]];
                if ((graphProps.meta.isPOP && tab.name === UNIQUE_USERS_TAB_NAME) || !tab.enabled) {
                    value = undefined;
                }
                return { ...tab, value };
            });
        }
        return tabs;
    }, [
        availableTabs,
        tableDataQuery.data,
        graphProps.meta.is28Days,
        graphProps.meta.isSingleMode,
        graphProps.meta.isPOP,
        params.webSource,
    ]);

    const setSelected = (newSelectedTab) => {
        const { isLocked, openHookModal } = newSelectedTab;
        if (isLocked) {
            openHookModal();
            return;
        }
        const tabTypeForTracking = getTabTypeForTracking(newSelectedTab.type);
        const tabNameForTracking =
            newSelectedTab.name === NEW_VS_RETURNING_TAB_NAME ? "New Users" : newSelectedTab.name;
        allTrackers.dispatchTrackEvent(
            "Metric Button",
            "click",
            `Over Time Graph/${tabTypeForTracking}/${tabNameForTracking}`,
            { UnbouncedFilter: unBounced },
        );
        swNavigator.updateParams({ [selectedMetricQueryParam]: newSelectedTab.name || null });
    };
    const [totalCount, setTotalCount] = useState();

    const isFromAlternativeSources = useMemo(() => {
        const isAlternative = !!tableDataQuery.data?.FromAlternativeSources;
        // if isFromAlternativeSources is true then we want to set the monthly visits tab as selected:
        if (isAlternative) {
            setSelected(availableTabs[0]);
        }
        return isAlternative;
    }, [tableDataQuery.data]);

    const MTDShown = useMemo(
        () =>
            !showBetaBranchData &&
            !DurationService.getDurationData(duration).raw?.isCustom &&
            duration !== "28d" &&
            !isFromAlternativeSources,
        [comparedDuration, duration, showBetaBranchData, isFromAlternativeSources],
    );

    const tabTypeForTracking = useMemo(() => getTabTypeForTracking(selectedTab.type), [selectedTab.type]);

    const MTDAvailable = useMemo(
        () => MTDShown && !unsupportedMTDTabs.includes(selectedTab.name),
        [MTDShown, selectedTab],
    );
    const unBouncedAvailable = useMemo(
        () =>
            !unsupportedUnBouncedTabs.includes(selectedTab.name) &&
            !graphProps.meta.isPOP &&
            !graphProps.showGAApprovedData,
        [selectedTab, graphProps.meta.isPOP, graphProps.showGAApprovedData],
    );

    // the unBounced url param needs to be set to false when the GA toggle is on in order to
    // cause the correct tabs to display based on each tabs isAvailable() method.
    useEffect(() => {
        if (graphProps.showGAApprovedData && String(unBounced).toLowerCase() === "true") {
            proNavigator.updateParams({ [unBouncedQueryParam]: false });
        }
    }, [graphProps.showGAApprovedData, unBounced]);

    const isMTDActive = useMemo(() => MTDAvailable && String(mtd).toLowerCase() === "true", [MTDAvailable, mtd]);
    const isUnBouncedActive = useMemo(
        () => unBouncedAvailable && String(unBounced).toLowerCase() === "true" && !graphProps.showGAApprovedData,
        [unBouncedAvailable, unBounced, graphProps.showGAApprovedData],
    );
    const toggleMTD = useCallback(() => {
        if (MTDAvailable) {
            allTrackers.trackEvent("Checkbox", "toggle", `Over Time Graph/${tabTypeForTracking}/MTD`);
            swNavigator.updateParams({ [monthToDateQueryParam]: !isMTDActive }, { replace: true });
        }
    }, [MTDAvailable, isMTDActive]);

    const toggleUnBounced = useCallback(() => {
        if (unBouncedAvailable) {
            allTrackers.dispatchTrackEvent(
                "Checkbox",
                unBounced === true ? "remove" : "add",
                `Over Time Graph/${tabTypeForTracking}/Unbounced`,
                { metricName: selectedTab.name, filterName: "unBounced" },
            );
            swNavigator.updateParams({ [unBouncedQueryParam]: !isUnBouncedActive }, { replace: true });
        }
    }, [unBouncedAvailable, isUnBouncedActive, unBounced, selectedTab.name]);

    const mtdTooltipText = getMonthsToDateTooltipText(MTDAvailable, isMTDActive);
    const [excelLink, setExcelLink] = React.useState(null);

    useEffect(() => {
        if (excelLink) {
            setExcelLink((v) => `/${v}`);
        }
    }, [excelLink]);

    // BI event for Excel export from the Over Time widget:
    // event_name=Table_<Widget>, event_sub_name=Excel, event_sub_sub_name=<selected metric title>
    const onExcelClick = React.useCallback(() => {
        allTrackers.dispatchTrackEvent(
            "Download",
            "submit-ok",
            `Table_${getWidgetNameForTracking(selectedTab.type)}/Excel/${graphProps.name}`,
            { engaged_only: isUnBouncedActive },
        );
    }, [selectedTab.type, graphProps.name, isUnBouncedActive]);
    const curGraphProps = React.useMemo(
        () => ({
            ...graphProps,
            setTotalCount,
            meta: {
                ...graphProps.meta,
                MTDAvailable,
                isMTDActive,
                isUnBouncedActive,
                isSingleCompare,
                showBetaBranchData,
                isFromAlternativeSources,
            },
            toggleMTD,
            updateExcelLink: setExcelLink,
        }),
        [graphProps, MTDAvailable, isMTDActive, toggleMTD, setExcelLink, isSingleCompare, showBetaBranchData],
    );
    const downloadExcelPermitted = swSettings.current.resources.IsExcelAllowed;

    const shouldDisplayInsights =
        hasOpenAiInsightsPermissions() &&
        graphProps.meta.isSingleMode &&
        !graphProps.meta.is28Days &&
        !showGAApprovedData &&
        selectedTab.supportInsights;

    return graphProps.meta.isSingleMode &&
        (tableDataQuery.state === useLoading.STATES.LOADING || tableDataQuery.state === useLoading.STATES.INIT) ? (
        <Loader height="500" />
    ) : (
        <>
            <TitleContainer removeBorderButton={!graphProps.meta.isSingleMode}>
                <FlexRow
                    alignItems={"center"}
                    justifyContent={"center"}
                    gap={"16px"}
                >
                    <HeaderTitle>{translate(titleKey)}</HeaderTitle>
                    {!isGADataLoading && graphProps.meta.isSingleMode && gaConnectionSupported ? (
                        <GA4VerifiedContainer
                            hasGAToken={hasGAToken}
                            isPrivate={gaPrivacyStatus === EPrivacyStatus.Private ? hasGAToken : null}
                        />
                    ) : null}
                </FlexRow>
                <RightSection>
                    <PlainTooltip
                        enabled={true}
                        tooltipContent={translate(
                            unBouncedAvailable
                                ? "wa.traffic.engagement.over.time.unbounce.checkbox.tooltip"
                                : "wa.traffic.engagement.over.time.unbounce.disabled.checkbox.tooltip",
                        )}
                    >
                        <UnbounceCheckboxWrapper>
                            <Checkbox
                                selected={isUnBouncedActive}
                                label={translate("wa.traffic.engagement.over.time.unbounce.checkbox")}
                                onClick={toggleUnBounced}
                                isDisabled={!unBouncedAvailable}
                            />
                        </UnbounceCheckboxWrapper>
                    </PlainTooltip>
                    {showBetaBranchData && !isSingleCompare ? <BetaBranchLabel /> : null}
                    {MTDShown ? (
                        <PlainTooltip
                            enabled={true}
                            tooltipContent={mtdTooltipText}
                        >
                            <MTDTitle
                                onClick={toggleMTD}
                                isDisabled={!MTDAvailable}
                            >
                                <OnOffSwitch
                                    isSelected={isMTDActive}
                                    onClick={noop}
                                    isDisabled={!MTDAvailable}
                                />
                                <MTDLabel>{i18n("wa.traffic.engagement.over.time.mtd.toggle_label")}</MTDLabel>
                            </MTDTitle>
                        </PlainTooltip>
                    ) : null}
                    {excelLink ? (
                        <DownloadButtonMenu
                            Excel={true}
                            downloadUrl={excelLink}
                            trackingFunction={onExcelClick}
                            excelLocked={!downloadExcelPermitted}
                            PaidExcel={isPaidExcelPermitted}
                            totalCount={totalCount}
                            showExcelEngagementPopup={showExcelEngagementPopup}
                        />
                    ) : null}
                </RightSection>
            </TitleContainer>
            {shouldDisplayInsights ? <Insights apiParams={apiParams} /> : null}
            {curGraphProps.meta.isSingleMode ? (
                <SingleModeTabs
                    availableTabs={tabs}
                    setSelected={setSelected}
                    selectedTab={
                        isFromAlternativeSources
                            ? { ...selectedTab, format: "abbrNumberWithNASupportVisits" }
                            : selectedTab
                    }
                    hasDedupeClaim={hasDedupeClaim}
                    graphProps={curGraphProps}
                />
            ) : (
                <CompareModeTabs
                    availableTabs={tabs}
                    selectedTab={selectedTab}
                    setSelected={setSelected}
                    graphProps={curGraphProps}
                    hasDedupeClaim={hasDedupeClaim}
                />
            )}
        </>
    );
};

const SingleModeTabs = ({ availableTabs, selectedTab, setSelected, graphProps, hasDedupeClaim }) => {
    return (
        <SingleModeTabsWrapper>
            <ScorableTabs
                tabs={availableTabs}
                selectedTab={selectedTab}
                setSelected={setSelected}
            >
                {getTabsPanels({ availableTabs, selectedTab, hasDedupeClaim, graphProps })}
            </ScorableTabs>
        </SingleModeTabsWrapper>
    );
};

const getTooltipMetricName = (timeGranularity: string, metricName: string, selectedTab: any) => {
    const i18n = i18nFilter();
    const lowerTabName = selectedTab.name?.toLowerCase?.();

    if (lowerTabName === "visits") {
        switch (timeGranularity) {
            case "Monthly":
                return i18n("wa.ao.graph.avgvisits.tooltip.mothly");
            case "Weekly":
                return i18n("wa.ao.graph.avgvisits.tooltip.weekly");
            default:
                return i18n("wa.ao.graph.avgvisits.tooltip.daily");
        }
    }

    const key = `wa.ao.graph.${lowerTabName}.tooltip`;
    const translated = i18n(key);

    return translated !== key ? translated : metricName;
};

const getTabsPanels = ({ availableTabs, selectedTab, hasDedupeClaim, graphProps }) => {
    graphProps = {
        ...graphProps,
        getChartTooltipHeaders: (timeGranularity: string) => {
            return [
                {
                    position: 0,
                    displayName: translate("website.analysis.traffic.and.engagement.chart.tooltip.domain"),
                },
                {
                    position: 2,
                    displayName: translate("website.analysis.traffic.and.engagement.chart.tooltip.change"),
                },
                {
                    position: 1,
                    displayName: getTooltipMetricName(timeGranularity, graphProps.name, selectedTab),
                },
            ];
        },

        meta: {
            ...graphProps.meta,
            isSingleMode: graphProps.meta.isSingleMode && !graphProps.meta.isSingleCompare, // if comparing for single, then get config of compare (old vs new)
            isCategoryActive:
                !unsupportedCategoryTabs.includes(selectedTab.name) &&
                !graphProps.meta.isPOP &&
                graphProps.params?.category &&
                graphProps.params?.category !== noCategoryItem.id,
        },
        chartKey: selectedTab.type, // for tracking
        selectedWidgetTab: selectedTab.name,
    };

    return availableTabs.map((tab, index) => {
        switch (tab.name) {
            case "Visits":
                return (
                    <TabPanelStyled key={`${tab.name}-${index}`}>
                        <Visits {...graphProps} />
                    </TabPanelStyled>
                );
            case "UniqueUsers":
                return (
                    <TabPanelStyled key={`${tab.name}-${index}`}>
                        <UniqueUsers {...graphProps} />
                    </TabPanelStyled>
                );
            case NEW_VS_RETURNING_TAB_NAME:
                return (
                    <TabPanelStyled key={`${tab.name}-${index}`}>
                        <NewVsReturning {...graphProps} />
                    </TabPanelStyled>
                );
            case "DeduplicatedAudience":
                return (
                    <TabPanelStyled key={`${tab.name}-${index}`}>
                        {hasDedupeClaim ? <DeduplicatedAudience {...graphProps} /> : <DeduplicationLockScreen />}
                    </TabPanelStyled>
                );
            case "AvgVisitDuration":
                return (
                    <TabPanelStyled key={`${tab.name}-${index}`}>
                        <AvgVisitDuration {...graphProps} />
                    </TabPanelStyled>
                );
            case "PagesPerVisit":
                return (
                    <TabPanelStyled key={`${tab.name}-${index}`}>
                        <PagesPerVisit {...graphProps} />
                    </TabPanelStyled>
                );
            case "BounceRate":
                return (
                    <TabPanelStyled key={`${tab.name}-${index}`}>
                        <BounceRate {...graphProps} />
                    </TabPanelStyled>
                );
            case "PageViews":
                return (
                    <TabPanelStyled key={`${tab.name}-${index}`}>
                        <PageViews {...graphProps} />
                    </TabPanelStyled>
                );
            default:
                return null;
        }
    });
};

const CompareModeTabs = ({ selectedTab, availableTabs, setSelected, graphProps, hasDedupeClaim }) => {
    const i18n = i18nFilter();
    const selectedIndexByName = availableTabs.findIndex((tab) => tab.name === selectedTab.name);

    return (
        <Tabs
            selectedIndex={selectedIndexByName >= 0 ? selectedIndexByName : 0}
            onSelect={(index) => setSelected(availableTabs[index])}
        >
            <TabWrapperStyle>
                {availableTabs.map((tab) => (
                    <CompareTab
                        key={tab.id ?? `${tab.type}-${tab.name}`}
                        tooltipText={i18n(tab.tooltip)}
                        disabled={!tab.enabled}
                    >
                        <Icon
                            size="xs"
                            iconName={tab.iconName}
                        />
                        {i18n(tab.getTitle ? tab.getTitle() : tab.title)}
                        {tab.isLocked ? (
                            <span style={{ marginLeft: 4 }}>
                                <Crown
                                    size="xs"
                                    color={colorsPalettes.indigo[300]}
                                />
                            </span>
                        ) : tab.labelText ? (
                            <BadgeContainer disabled={!tab.enabled}>
                                <NavItemBadge type={tab.labelText} />
                            </BadgeContainer>
                        ) : null}
                    </CompareTab>
                ))}
            </TabWrapperStyle>
            {getTabsPanels({ availableTabs, selectedTab, hasDedupeClaim, graphProps })}
        </Tabs>
    );
};
